import { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { queryClient } from "../../App";
import moment from "moment";
import { decodeToken } from "react-jwt";
import { attachAuthHeader } from "../../utils/attachAuthHeader";
import {
  isImpersonating,
  exitImpersonation,
  decodeImpersonatedUser,
  clearImpersonationBackup,
} from "../../helpers/impersonate";

// Drop every react-query cache entry so the next tenant doesn't see
// the previous tenant's data. Called from both ends of the auth
// boundary (logout, refresh-token death, fresh login) for defence in
// depth — Danylo 2026-05-15 cross-tenant leak repro.
const purgeQueryCache = () => {
  try { queryClient.clear(); } catch { /* queryClient missing in tests */ }
};

// Singleton interceptor registration. useAuth() is called from many places
// (AuthProvider, layout, TicketsCustom, SettingsCustom...). Previously each
// call registered fresh request/response interceptors, so axios ended up
// running the same logic 5-10x per request and triggered duplicate refresh
// calls on 403. We register once at module load and broadcast auth-state
// changes to subscribed hook instances via a tiny pub/sub.
const authStateSubscribers = new Set();
const broadcastAuth = (next) => {
  authStateSubscribers.forEach((cb) => {
    try { cb(next); } catch { /* subscriber dead */ }
  });
};

let interceptorsRegistered = false;

// Singleton in-flight refresh. Multiple parallel 401s used to each spin
// up their own POST /auth/refresh_token, which is fine when the refresh
// token is reusable but actively destructive when the backend rotates it
// (single-use): the second call 401s and clears the session that the
// first call just rotated. Coalesce them onto one shared promise.
let refreshPromise = null;
function refreshTokenOnce() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = api
    .post("/auth/refresh_token")
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function registerInterceptorsOnce() {
  if (interceptorsRegistered) return;
  interceptorsRegistered = true;

  api.interceptors.request.use(
    (config) => {
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const parsedToken = JSON.parse(token);
          const { headers, applied } = attachAuthHeader(
            config.headers,
            parsedToken
          );
          config.headers = headers;
          if (applied) broadcastAuth(true);
        }
      } catch (_) {
        // ignore malformed tokens and continue without auth header
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Session expiry helper — clears storage + broadcasts + redirects to /login
  // when the user isn't already on a public auth page (prevents redirect
  // loops when /login itself somehow throws 401).
  const expireSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("companyId");
    localStorage.removeItem("userId");
    clearImpersonationBackup();
    delete api.defaults.headers.Authorization;
    purgeQueryCache();
    broadcastAuth(false);
    if (typeof window !== "undefined") {
      const path = window.location.pathname || "";
      const PUBLIC_AUTH_PATHS = [
        "/login",
        "/signup",
        "/forgot-password",
        "/reset-password",
        "/auth/sso",
      ];
      const onPublic = PUBLIC_AUTH_PATHS.some(
        (p) => path === p || path.startsWith(p + "/")
      );
      if (!onPublic) {
        // Use replace so the expired page is not in history (Back from /login
        // would re-trigger the same 401 chain).
        window.location.replace("/login?expired=1");
      }
    }
  };

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config || {};
      const status = error?.response?.status;
      const reqUrl = originalRequest.url || "";

      // CRITICAL: never try to refresh when the failing request IS the
      // refresh endpoint itself. The `_retry` flag is per-request, but
      // every refresh attempt creates a *new* request with a fresh
      // `_retry=undefined`, so the per-request guard alone cannot stop
      // a loop. With a stale/invalid refresh token, a single failing
      // call triggers a chain:
      //   call X → 401 → refresh → 401 → refresh → 401 → ...
      // hammering the server at >20 req/s until the tab is closed.
      // This guard breaks the chain at the first refresh failure.
      const isRefreshCall =
        reqUrl.endsWith("/auth/refresh_token") ||
        reqUrl.includes("/auth/refresh_token?");

      // Refresh on either 401 OR 403 — different backends signal expiry
      // differently and we previously only refreshed on 403, leaving 401s
      // to fall through and silently log out the user.
      // While impersonating, a 401/403 must END impersonation, never refresh:
      // refresh uses the admin jrt cookie and would re-elevate to super-admin.
      if ((status === 401 || status === 403) && !isRefreshCall && isImpersonating()) {
        exitImpersonation();
        return Promise.reject(error);
      }

      if (
        (status === 401 || status === 403) &&
        !originalRequest._retry &&
        !isRefreshCall
      ) {
        originalRequest._retry = true;
        try {
          // All concurrent 401s share one in-flight refresh — see
          // refreshTokenOnce() at the top of this file.
          const { data } = await refreshTokenOnce();
          if (data?.token) {
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            broadcastAuth(true);
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${data.token}`;
            return api(originalRequest);
          }
        } catch (refreshErr) {
          // Refresh failed — clear session, notify subscribers, redirect.
          expireSession();
          return Promise.reject(refreshErr);
        }
      }

      // If the refresh endpoint itself returned 401/403, the session is
      // gone — clear it once and stop. Falling through here without this
      // would loop the whole chain on the next call from anywhere.
      if (isRefreshCall && (status === 401 || status === 403)) {
        expireSession();
        return Promise.reject(error);
      }

      if (status === 401) {
        expireSession();
      }
      return Promise.reject(error);
    }
  );
}

registerInterceptorsOnce();

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});

  const socketManager = useContext(SocketContext);

  // Subscribe to interceptor auth-state broadcasts so this hook stays in
  // sync with whatever the (singleton) interceptors learn from API calls.
  const subscribedRef = useRef(false);
  if (!subscribedRef.current) {
    subscribedRef.current = true;
    authStateSubscribers.add(setIsAuth);
  }
  useEffect(() => {
    return () => {
      authStateSubscribers.delete(setIsAuth);
      subscribedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        if (isImpersonating()) {
          // Self-contained scoped session — never call /auth/refresh_token (it
          // uses the admin jrt cookie and would re-elevate to admin). Fetch the
          // full tenant user with the impersonation token instead (GET /users/:id
          // includes queues + company); if it 401s, the response interceptor
          // ends impersonation. Fall back to the minimal token payload on a
          // non-401 error so the session still authenticates.
          const impUser = decodeImpersonatedUser();
          try {
            if (impUser) {
              const { data } = await api.get(`/users/${impUser.id}`);
              setUser(data);
              setIsAuth(true);
            }
          } catch (err) {
            if (impUser) {
              setUser(impUser);
              setIsAuth(true);
            }
          }
          setLoading(false);
          return;
        }
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user);
        } catch (err) {
          // Apple App Store Guideline 2.1(a): this effect runs at app launch.
          // A failed refresh (expired session, server restart, network blip)
          // must not flash an error toast before the user even sees the login
          // form — fall through to the login screen silently instead.
          // No cleanup needed here: on 401/403 the response interceptor's
          // expireSession() already cleared storage and redirected to /login;
          // on network/5xx errors the token is intentionally kept so the next
          // launch can retry once the server is back.
          console.error("auth: token refresh failed at launch", err?.message || err);
        }
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (!companyId) {
		return () => {};
	}
    const socket = socketManager.GetSocket(companyId);

    const onCompanyUserUseAuth = (data) => {
      if (data.action === "update" && data.user.id === user.id) {
        setUser(data.user);
      }
    }

    socket.on(`company-${companyId}-user`, onCompanyUserUseAuth);

    return () => {
      // Detach ONLY this hook's listener — calling socket.disconnect()
      // here ripped the entire shared socket out from under every
      // other consumer (TicketListCustom, NotificationsPopover, etc.)
      // every time `user` changed, which dropped real-time updates
      // platform-wide for a fraction of a second on each user-state
      // refresh.
      socket.off(`company-${companyId}-user`, onCompanyUserUseAuth);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      // Purge any cached data from a previous tenant BEFORE we install
      // the new token. If user A's session expired and bounced them
      // here, react-query is still holding A's tickets/contacts under
      // composite keys that don't include companyId — without this
      // purge, user B will briefly see A's data on first render.
      purgeQueryCache();

      const { data } = await api.post("/auth/login", userData);
      const { token } = data;

      const { companyId, userId } = decodeToken(token);

      moment.locale('he-IL');
      // Defensive read: super-admins, freshly-signed-up tenants, or users
      // whose Company row was deleted may come back without `user.company`.
      // Treat missing dueDate as "no expiry" so we don't crash the login
      // flow with a silent TypeError that the catch swallows below.
      const dueDate = data?.user?.company?.dueDate || null;
      const vencimento = dueDate ? moment(dueDate).format("DD/MM/yyyy") : null;

      const before = !dueDate || moment(moment().format()).isBefore(dueDate);
      const dias = dueDate
        ? moment.duration(moment(dueDate).diff(moment(moment()).format())).asDays()
        : Infinity;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(token));
        localStorage.setItem("companyId", companyId);
        localStorage.setItem("userId", userId);
        if (vencimento) {
          localStorage.setItem("companyDueDate", vencimento);
        } else {
          localStorage.removeItem("companyDueDate");
        }
        // Auto-apply the tenant's preferred UI language on first login, so a
        // Hebrew-only company sees Hebrew labels immediately without having
        // to dig through the user menu. We only set it when localStorage.language
        // is unset (= user has never explicitly chosen a UI locale) — once
        // they pick a language we respect that override on subsequent logins.
        // Maps any unsupported value to the closest supported one rather than
        // letting i18next fall back to its base English bundle silently.
        const SUPPORTED_LANGS = ["he", "en", "ru", "ar", "es"];
        const storedLang = localStorage.getItem("language");
        const companyLang = (data?.user?.company?.language || "").toLowerCase();
        if (!storedLang && companyLang) {
          const chosen = SUPPORTED_LANGS.includes(companyLang) ? companyLang : "en";
          localStorage.setItem("language", chosen);
          try {
            i18n.changeLanguage(chosen);
          } catch {
            /* non-fatal — App.js useEffect picks it up on next mount */
          }
        }
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        if (Number.isFinite(dias) && Math.round(dias) < 5) {
          toast.warn(`your plan is about to expire ${Math.round(dias)} ${Math.round(dias) === 1 ? 'day' : 'days'} `);
        }
        // Post-auth routing. Order matters: super wins over admin
        // because super-admins are platform operators (Whapiz shell),
        // not tenant operators (GoDeal CRM home). The Whapiz admin
        // shell guards itself again on mount, so the redirect alone
        // is not the auth boundary — backend isSuper middleware is.
        if (data.user.super === true) {
          history.push("/admin");
        } else if (data.user.profile === "admin") {
          history.push("/");
        } else if (data.user.profile === "supervisor") {
          // Supervisor lands on tickets (sees all), not the admin dashboard
          history.push("/tickets");
        } else {
          history.push("/tickets");
        }
        setLoading(false);
      } else {
        toastError(`Plan expired ${vencimento}.
Talk to support to more details! `);
        setLoading(false);
      }

    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
    } catch (err) {
      // Best-effort: a failed server-side logout must NOT leave the user
      // locally authenticated (token/cache/session would stay live). Tear the
      // local session down unconditionally below.
      toastError(err);
    }
    {
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      clearImpersonationBackup();
      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");
      api.defaults.headers.Authorization = undefined;
      purgeQueryCache();
      // Tell every other useAuth() instance still subscribed to the
      // singleton broadcast that we're logged out. Without this, only
      // the hook instance whose handleLogout was called flips its
      // isAuth flag — sibling instances (layout, MainListItems, etc.)
      // still believe the user is authenticated until their next
      // re-render is triggered by something else.
      broadcastAuth(false);
      setLoading(false);
      // Hard reload to /login: kills queryClient + every component's
      // local state + the live socket connection. history.push leaves
      // all three in memory, which is exactly how Danylo's leak was
      // possible (NotificationsPopOver's merge-with-prev-state held
      // socket-pushed tickets from the previous tenant through a
      // logout/login cycle 2026-05-15).
      window.location.href = "/login";
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
  };
};

export default useAuth;
